import { useCallback, useEffect, useRef, useState } from 'react';
import type { MusicVideoEditorResponse } from 'fym-shared/src/music24/musicVideoApi';
import { cutCreateError, type CutCreateError } from '../doc/musicVideoErrors';
import { useCreateMusicVideoCutMutation } from '../musicVideoEditorApi';
import { needsFirstCut } from './openCutChoice';

interface UseFirstCutProps {
  accountId: number;
  catalogItemUuid: string;
  data: MusicVideoEditorResponse | undefined;
  /** False for the example artist (read only) and with no master. */
  enabled: boolean;
}

/**
 * The editor never opens empty (design e1, the cover editor rule): a song with a master and no cut
 * gets cut 1 at once (`POST /cuts` with no body), and the director makes it. One request per open;
 * a failure shows the error with "Try again". `error` says why: `limit` (409 `M24_CUT_LIMIT`) or
 * any other failure.
 */
export function useFirstCut({ accountId, catalogItemUuid, data, enabled }: UseFirstCutProps) {
  const [createCut] = useCreateMusicVideoCutMutation();
  const [error, setError] = useState<CutCreateError | null>(null);
  const [createdUuid, setCreatedUuid] = useState<string | null>(null);
  const started = useRef(false);

  const run = useCallback(() => {
    started.current = true;
    setError(null);
    createCut({ accountId, catalogItemUuid })
      .unwrap()
      .then(({ cut }) => setCreatedUuid(cut.uuid))
      .catch((caught: unknown) => {
        console.error('Music video editor: the first cut was not made', caught);
        setError(cutCreateError(caught));
      });
  }, [accountId, catalogItemUuid, createCut]);

  useEffect(() => {
    if (enabled && !started.current && needsFirstCut(data)) {
      run();
    }
  }, [data, enabled, run]);

  return { isFailed: error !== null, error, retry: run, createdUuid };
}
